import Phaser from 'phaser';
import { nameText } from '../../content/names';
import type { FriendCard, FriendStats, PlayerName } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { nicknameOf } from '../nicknames';
import { blockPlayer, friendCard, removeFriend } from '../online';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { formatTime } from '../ui/widgets';
import type { NicknameData } from './NicknameScene';
import type { RaceData } from './RaceScene';
import { wipeTo } from './WipeScene';

/** A friend, as their row on CLUB > YOU > FRIENDS had them. */
export interface FriendData {
  player: string;
  name: PlayerName;
  /** When they became friends (ms since 1970). */
  at: number;
  online?: boolean;
}

/** One line of the table: the label and each side's value, the higher one better unless `lower`. */
interface Line {
  label: string;
  you: number | null;
  them: number | null;
  text: (n: number) => string;
  lower?: boolean;
  /** A second, dim line under the values (the Daily's ranks). */
  under?: { you: string | null; them: string | null };
}

/** "OCT 26" for a time. */
const since = (ms: number) =>
  new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

/**
 * A friend's card (docs/FRIENDS.md, "The friend's card"; owner, 2026-10-29), from their row on
 * CLUB > YOU > FRIENDS: who they are, how this player stands beside them (the farthest campaign
 * lawn, today's Daily, the Daily streaks, the best Endless Lawns score: all from boards they
 * already post to), PLAY TOGETHER (the VERSUS lobby, inviting them), and NICKNAME, REMOVE and
 * BLOCK (both asked twice).
 */
export class FriendScene extends Phaser.Scene {
  private friend!: FriendData;
  private card: FriendCard | null = null;
  private loading = true;
  /** REMOVE or BLOCK tapped once (tapped again, it happens). */
  private sure: 'remove' | 'block' | null = null;
  private note: string | null = null;

  constructor() {
    super('Friend');
  }

  init(data: FriendData): void {
    this.friend = data;
    this.card = null;
    this.loading = true;
    this.sure = null;
    this.note = null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void friendCard(this.friend.player).then((card) => {
      this.card = card;
      this.loading = false;
      this.redraw();
    });
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    const top = drawClubBar(this, 'you') + 4;
    const g = this.add.graphics();
    const f = this.friend;
    const backY = height - TABBAR_H - 28;
    bevelButton(this, 6, backY, width - 12, 22, 'BACK', () => wipeTo(this, 'Friends'));

    // Who: the nickname over the name it stands for, online now, friends since.
    const nickname = nicknameOf(f.player);
    let y = top;
    if (f.online) g.fillStyle(UI.good).fillRect(6, y + 2, 4, 4);
    label(this, 13, y, nickname ?? nameText(f.name), 1, UI.accent);
    if (f.online) label(this, width - 6, y, 'Online now', 1, UI.good).setOrigin(1, 0);
    y += 11;
    if (nickname) {
      label(this, 13, y, nameText(f.name), 1, UI.dim);
      y += 11;
    }
    label(this, 13, y, `Friends since ${since(f.at)}`, 1, UI.dim);
    y += 15;

    // You beside them.
    const tableH = 92;
    navyPanel(g, 4, y, width - 8, tableH);
    const youX = 82;
    const themX = 136;
    const head = nickname && nickname.length <= 8 ? nickname : 'THEM';
    label(this, youX, y + 5, 'YOU', 1, UI.dim);
    label(this, themX, y + 5, head, 1, UI.dim);
    const card = this.card;
    if (!card) {
      const text = this.loading ? 'Loading...' : "Can't reach the server right now.";
      label(this, width / 2, y + 38, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 20)
        .setCenterAlign();
    } else {
      let ly = y + 17;
      for (const line of lines(card.you, card.them)) {
        label(this, 10, ly, line.label, 1, UI.dim);
        const better = (a: number | null, b: number | null) =>
          a !== null && (b === null || (line.lower ? a < b : a > b));
        const cell = (x: number, v: number | null, wins: boolean) =>
          label(this, x, ly, v === null ? '-' : line.text(v), 1, wins ? UI.good : UI.text);
        cell(youX, line.you, better(line.you, line.them));
        cell(themX, line.them, better(line.them, line.you));
        ly += 11;
        if (line.under && (line.under.you !== null || line.under.them !== null)) {
          label(this, youX, ly - 1, line.under.you ?? '', 1, UI.dim);
          label(this, themX, ly - 1, line.under.them ?? '', 1, UI.dim);
          ly += 10;
        }
      }
    }
    y += tableH + 6;

    // What to do.
    bevelButton(this, 6, y, width - 12, 20, 'PLAY TOGETHER', () => this.play(), true);
    y += 24;
    const third = Math.floor((width - 20) / 3);
    bevelButton(this, 6, y, third, 16, 'NICKNAME', () =>
      wipeTo(this, 'Nickname', {
        player: f.player,
        name: f.name,
        back: { scene: 'Friend', data: f },
      } satisfies NicknameData),
    );
    bevelButton(
      this,
      10 + third,
      y,
      third,
      16,
      this.sure === 'remove' ? 'SURE?' : 'REMOVE',
      () => void this.act('remove'),
    );
    bevelButton(
      this,
      14 + 2 * third,
      y,
      width - 20 - 2 * third,
      16,
      this.sure === 'block' ? 'SURE?' : 'BLOCK',
      () => void this.act('block'),
    );
    y += 22;
    const why =
      this.note ??
      (this.sure === 'remove'
        ? 'Tap again: no longer friends, for either of you.'
        : this.sure === 'block'
          ? 'Tap again: no longer friends, and no invites, requests or alerts between you.'
          : null);
    if (why) {
      label(this, width / 2, y, why, 1, this.note ? UI.danger : UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }
  }

  /** PLAY TOGETHER: the VERSUS lobby, its INVITE going straight to this friend. */
  private play(): void {
    const f = this.friend;
    wipeTo(this, 'Race', {
      friend: { player: f.player, name: f.name, back: f },
    } satisfies RaceData);
  }

  /** REMOVE or BLOCK: the first tap asks, the second does it (then back to the list). */
  private async act(what: 'remove' | 'block'): Promise<void> {
    if (this.sure !== what) {
      this.sure = what;
      this.note = null;
      this.redraw();
      return;
    }
    this.sure = null;
    const done =
      what === 'remove'
        ? await removeFriend(this.friend.player)
        : await blockPlayer(this.friend.player);
    if (done) {
      wipeTo(this, 'Friends');
      return;
    }
    this.note = "Can't reach the server right now.";
    this.redraw();
  }
}

/** The table's lines, this player's numbers beside the friend's. */
function lines(you: FriendStats, them: FriendStats): Line[] {
  return [
    { label: 'CAMPAIGN', you: you.lawn, them: them.lawn, text: (n) => `Lawn ${n}` },
    {
      label: 'DAILY TODAY',
      you: you.daily?.ticks ?? null,
      them: them.daily?.ticks ?? null,
      text: (n) => formatTime(n, TICK_RATE),
      lower: true,
      under: {
        you: you.daily ? `#${you.daily.rank}` : null,
        them: them.daily ? `#${them.daily.rank}` : null,
      },
    },
    {
      label: 'STREAK',
      you: you.streak?.current ?? null,
      them: them.streak?.current ?? null,
      text: days,
    },
    {
      label: 'LONGEST',
      you: you.streak?.longest ?? null,
      them: them.streak?.longest ?? null,
      text: days,
    },
    { label: 'ENDLESS', you: you.endless, them: them.endless, text: String },
  ];
}
